import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import PrivateLayout from "components/Layout/PrivateLayout"
import { privateRoutes } from "router/routes"

import { useAlert, useOperator } from "hooks"
import {
	bikeRegisteredEnum,
	BikeRegistrationActionType,
	useBikeRegistrationProcess,
	useBikesStore,
} from "../hooks"
import { Bike, ErrorResponse } from "models"

import { Notifications, AddBikeButton, BikeCard } from "../components"
import { pageStatusEnum } from "utils"
import { MenuSquareIcon, MenuLineIcon, Spinner } from "components/Elements"
import { Seo } from "components/Seo"
import { BikeTable } from "../components/Table"

// prettier-ignore
export type PageAction = { type: "LOADING" } | { type: "SUCCESS" } | { type: "ERROR" }
export type PageDispatch = (action: PageAction) => void

const CARD_VIEW = "card"
const LIST_VIEW = "list"

const pageReducer = (state: any, action: PageAction) => {
	switch (action.type) {
		case "LOADING": {
			return {
				status: pageStatusEnum.loading,
			}
		}
		case "SUCCESS": {
			return {
				status: pageStatusEnum.complete,
			}
		}
		case "ERROR": {
			return {
				status: pageStatusEnum.errored,
			}
		}
	}
}

export const DashboardPage: React.FC = () => {
	const [{ status }, dispatchPage] = React.useReducer(pageReducer, {
		status: pageStatusEnum.loading,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const navigate = useNavigate()
	const { currentOperator } = useOperator()

	const { bikeRegistrationState, bikeRegistrationDispatch } = useBikeRegistrationProcess()
	const { bikesStore, fetchBikes } = useBikesStore()
	const {
		// prettier-ignore
		bikesWithActionNeeded: bikesNotificationList,
		bikesWithNoActionNeeded: bikesList,
		isBikesFetch,
	} = bikesStore

	const isLoading = status === pageStatusEnum.loading
	const isComplete = status === pageStatusEnum.complete
	const isErrored = status === pageStatusEnum.errored

	const isBikeRegistrationStep =
		bikeRegistrationState.step === bikeRegisteredEnum.bikeRegistrationStep

	const handleFetchBikes = async () => {
		try {
			dispatchPage({ type: "LOADING" })
			fetchBikes()
			dispatchPage({ type: "SUCCESS" })
		} catch (error) {
			console.error(error)
			dispatchPage({ type: "ERROR" })
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		}
	}

	const hasBikes = (bikesList && bikesList.length) > 0 ?? false
	const hasBikesNotification =
		(bikesNotificationList && bikesNotificationList.length > 0) ?? false
	const displayAddBikeModal = isBikesFetch ? !hasBikes && !hasBikesNotification : false // display modal if no bikes and no notification

	const [view, setView] = React.useState<string>(CARD_VIEW)

	const isCardView = view === CARD_VIEW
	const isListView = view === LIST_VIEW

	React.useEffect(() => {
		// Prevent refetch of bikes
		if (isBikesFetch) dispatchPage({ type: "SUCCESS" })
		// get all the bikes
		if (!isBikesFetch) handleFetchBikes()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isBikesFetch])

	React.useEffect(() => {
		// Reset register bike process
		bikeRegistrationDispatch({
			type: BikeRegistrationActionType.INITIAL_STEP,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	React.useEffect(() => {
		// If the step changed to bikeRegistrationStep redirect to the corresponding page
		if (isBikeRegistrationStep) navigate(privateRoutes.registerBike.path)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bikeRegistrationState])

	// Design requirement: modal closing should not restore focus on triggering button (browser behavior).
	// Instead the focus is restored on dashboardTitleRef
	const dashboardTitleRef = React.useRef<HTMLHeadingElement>(null)

	return (
		<PrivateLayout>
			<div className="mx-auto p-2 lg:p-10">
				<div className="max-w-screen-3xl mx-auto px-4 py-8 lg:py-16 relative">
					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}

					{isComplete && (
						<>
							<Seo
								title={t("dashboard", {
									ns: "seo",
									operator: currentOperator.name,
								})}
							/>
							{bikesNotificationList && bikesNotificationList.length > 0 && (
								<Notifications
									bikes={bikesNotificationList}
									refreshBikes={handleFetchBikes}
								/>
							)}

							<div className="flex items-baseline justify-between w-100">
								<h1
									className="c-h1 leading-tight mb-4 md:mb-10"
									// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
									tabIndex={0}
									ref={dashboardTitleRef}
								>
									{t("dashboard.title", { ns: "bike" })}
								</h1>

								<div className="c-btn-g" role="group">
									<button
										title={t("dashboard.card_view", { ns: "bike" })}
										type="button"
										className={`c-btn-g__item ${
											isCardView ? "active" : ""
										} py-2 px-4 text-sm font-medium text-gray-900 hover:bg-blue30 focus:z-10 focus:ring-2 focus:ring-primary focus:bg-primary focus:text-white`}
										onClick={() => setView(CARD_VIEW)}
									>
										<MenuSquareIcon size="20px" />
									</button>
									<button
										title={t("dashboard.list_view", { ns: "bike" })}
										type="button"
										className={`c-btn-g__item ${
											isListView ? "active" : ""
										} py-2 px-4 text-sm font-medium text-gray-900 hover:bg-blue30 focus:z-10 focus:ring-2 focus:ring-primary focus:bg-primary focus:text-white`}
										onClick={() => setView(LIST_VIEW)}
									>
										<MenuLineIcon size="24px" style={{ marginTop: 6 }} />
									</button>
								</div>
							</div>

							<div
								className={`c-block  px-3 md:px-6 xl:px-10 pt-12 
									${!hasBikes ? "py-10" : "pt-10"} 
									${isListView ? "pb-12" : ""}
								`}
							>
								{/* Array list */}
								{isListView && <BikeTable bikesList={bikesList} />}

								{/* Card list */}
								{isCardView && (
									<div className="flex flex-wrap justify-evenly lg:justify-start">
										{!bikesList ||
											(bikesList.length === 0 && (
												<div className="w-full text-m pt-4 pb-8 text-center font-bold">
													{t("dashboard.no_bikes", { ns: "bike" })}
												</div>
											))}

										<div
											className={`${
												!bikesList || bikesList.length === 0
													? "mx-auto"
													: ""
											}`}
										>
											<AddBikeButton
												className="w-1/5 px-2 xl:mx-6 mb-12"
												refreshBikes={handleFetchBikes}
												forceTriggering={displayAddBikeModal}
												focusRedirectionRef={dashboardTitleRef}
											/>
										</div>

										{
											// prettier-ignore
											hasBikes &&
											bikesList.map((bike: Bike, index: number) => (
												<BikeCard bike={bike} key={index} className="mx-2 xl:mx-6 mb-12" />
											))
										}
									</div>
								)}
							</div>
						</>
					)}

					{isErrored && (
						<div className="flex justify-center my-8">
							{t("account.error", { ns: "auth" })}
						</div>
					)}
				</div>
			</div>
		</PrivateLayout>
	)
}
